'use client'

// Member Home dashboard — "My village". Everything in one place: coins,
// matches, bookings, saved events, following and pending reviews.
// Data comes from existing endpoints fetched in parallel (same pattern as
// /match and /activity) — /me/analytics already aggregates the snapshot
// numbers, so no dedicated dashboard endpoint is needed.

import { Fragment, useEffect, useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import {
  Calendar,
  Compass,
  Heart,
  Sparkles,
  Star,
  UserPlus,
  Users,
} from 'lucide-react'
import { createClient } from '~/lib/supabase/client'
import { env } from '~/env'
import { resolveImageUrl, defaultEventImage } from '~/lib/images'
import { summarizeLocation } from '~/lib/location'
import { formatEventDate } from '~/lib/event-dates'
import { AuthPromptDialog } from '~/components/auth/AuthPromptDialog'
import { JOIN_PROMPT_DESCRIPTION, JOIN_PROMPT_ICON, JOIN_PROMPT_TITLE } from './join-prompt-copy'
import { ReviewModal } from '~/components/reviews/ReviewModal'
import { FitBadge, bucketFromScore } from '~/components/matching/fit-badge'
import { PathCoinsWalletCard } from '~/components/path-coins/PathCoinsWalletCard'
import type { Recommendation } from '~/components/matching/recommendation'

// Path Collective visual system
const FONT_UI = 'font-[family-name:var(--font-rounded)]'
const FONT_HEADING = 'font-[family-name:var(--font-sora)]'
const FONT_BODY = 'font-[family-name:var(--font-nunito)]'

function getFirstImageUrl(rawUrl?: string | null, categories?: { name: string }[] | null): string {
  const url = rawUrl?.includes(',') ? rawUrl.split(',')[0]?.trim() : rawUrl
  return url ? resolveImageUrl(url) : defaultEventImage(categories)
}

function CardHead({
  icon: Icon,
  tint,
  title,
  subtitle,
  count,
  link,
}: {
  icon: React.ComponentType<{ className?: string }>
  tint: 'terra' | 'teal' | 'gold'
  title: string
  subtitle: string
  count?: number
  link?: { label: string; href: string }
}) {
  const tints = {
    terra: 'bg-[#EFD3C4] text-[#DE866D]',
    teal: 'bg-[#D4D9CE] text-[#3f6258]',
    gold: 'bg-[#F3DFBE] text-[#7a5414]',
  }
  return (
    <div className="flex items-center gap-3 mb-4">
      <div className={`w-[34px] h-[34px] rounded-[11px] flex items-center justify-center flex-shrink-0 ${tints[tint]}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <h2 className={`${FONT_UI} font-bold text-base text-[#2C2D2B] leading-tight`}>{title}</h2>
        <p className={`${FONT_BODY} text-xs text-[#8A8475]`}>{subtitle}</p>
      </div>
      {count !== undefined && count > 0 && (
        <span className={`${FONT_UI} ml-auto font-bold text-xs text-[#DE866D] bg-[#EFD3C4] min-w-6 h-6 rounded-full flex items-center justify-center px-2`}>
          {count}
        </span>
      )}
      {link && (
        <Link href={link.href} className={`${FONT_UI} ml-auto font-bold text-[13px] text-[#DE866D] hover:underline`}>
          {link.label} →
        </Link>
      )}
    </div>
  )
}

function EmptyBlock({ icon: Icon, title, hint }: { icon: React.ComponentType<{ className?: string }>; title: string; hint: string }) {
  return (
    <div className="bg-[#F7F0E6] rounded-xl p-7 text-center">
      <Icon className="h-8 w-8 text-[#D9CDB9] mx-auto mb-2" />
      <p className={`${FONT_UI} font-bold text-sm text-[#2C2D2B] mb-0.5`}>{title}</p>
      <p className={`${FONT_BODY} text-xs text-[#8A8475]`}>{hint}</p>
    </div>
  )
}

export default function HomeDashboardPage() {
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const [checkedAuth, setCheckedAuth] = useState(false)
  const [firstName, setFirstName] = useState<string>('')
  const [balance, setBalance] = useState<{ balance_available: number; balance_total: number; balance_redeemed: number } | null>(null)
  const [analytics, setAnalytics] = useState<any>(null)
  const [recommendations, setRecommendations] = useState<Recommendation[]>([])
  const [bookings, setBookings] = useState<any[]>([])
  const [savedEvents, setSavedEvents] = useState<any[]>([])
  const [following, setFollowing] = useState<any[]>([])
  const [friends, setFriends] = useState<any[]>([])
  // Top-right toggle on the Following card: facilitators you follow vs members you friended
  const [followTab, setFollowTab] = useState<'facilitators' | 'friends'>('facilitators')
  const [pendingReviews, setPendingReviews] = useState<any[]>([])
  const [reviewingEventId, setReviewingEventId] = useState<string | null>(null)
  // Visitors (no session) get the Home layout with a sign-up prompt over it.
  const [showVisitorPrompt, setShowVisitorPrompt] = useState(false)

  useEffect(() => {
    const load = async () => {
      const supabase = createClient()
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData.session?.access_token ?? null
      setAccessToken(token)
      setCheckedAuth(true)
      if (!token) { setShowVisitorPrompt(true); return }

      const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      const get = (path: string) =>
        fetch(`${env.NEXT_PUBLIC_API_URL}${path}`, { headers })
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null)

      // Everything in parallel; each section renders as its data lands.
      void get('/user').then((u) => {
        if (u?.full_name) setFirstName(String(u.full_name).split(' ')[0] ?? '')
      })
      void get('/me/path-coins/balance').then((b) => b && setBalance(b))
      void get('/me/analytics').then((a) => a && setAnalytics(a))
      void get('/events/recommendations').then((d) => {
        const recs = (d?.recommendations ?? []).slice(0, 3)
        setRecommendations(recs)
      })
      void get('/events/booked').then((e) => Array.isArray(e) && setBookings(e.filter((ev: any) => ev.status !== 'CANCELLED' && ev.booking_status !== 'REFUNDED').slice(0, 3)))
      void get('/events/saved').then((e) => Array.isArray(e) && setSavedEvents(e.slice(0, 3)))
      void get('/facilitator-followers').then((f) => Array.isArray(f) && setFollowing(f.filter((x: any) => x.facilitator?.user_type === 'FACILITATOR').slice(0, 4)))
      // Friends via the members endpoint, which enforces profile_public
      void get('/facilitator-followers/members').then((f) => Array.isArray(f) && setFriends(f.slice(0, 4)))
      void get('/reviews/pending').then((r) => Array.isArray(r) && setPendingReviews(r.slice(0, 3)))
    }
    void load()
  }, [])

  const monthEarned = analytics?.this_month?.pathcoins_earned ?? 0
  const totals = analytics?.totals

  const formatShort = (event: any) =>
    formatEventDate(event, 'en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })

  return (
    <div className="fixed inset-0 top-16 overflow-auto bg-[#F7F0E6]">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 pb-16">

        {/* ═══ Header ═══ */}
        <div className="flex flex-wrap items-end justify-between gap-5 mb-7">
          <div>
            <div className={`${FONT_BODY} font-bold text-xs tracking-[.16em] uppercase text-[#DE866D] mb-2`}>My village</div>
            <h1 className={`${FONT_HEADING} font-extrabold uppercase text-3xl sm:text-[38px] leading-none tracking-[.01em] text-[#2C2D2B]`}>
              {accessToken ? `Welcome back${firstName ? `, ${firstName}` : ''}` : 'Welcome to Path Collective'}
            </h1>
            <p className={`${FONT_BODY} text-sm text-[#5E594F] mt-2.5 max-w-[60ch]`}>
              Everything in one place — your coins, your matches, your bookings and the people you follow.
            </p>
          </div>
          <div className="flex gap-2.5 flex-wrap">
            <Link
              href="/match/game"
              className="inline-flex items-center gap-2 rounded-full bg-[#DE866D] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#c9755e]"
            >
              <Sparkles className="h-4 w-4" />
              Play matching game
            </Link>
          </div>
        </div>

        {/* ═══ Top grid: wallet + snapshot ═══ */}
        <div className="grid gap-5 lg:grid-cols-[380px_1fr]">
          {/* Path Coins wallet */}
          <PathCoinsWalletCard balance={balance} monthEarned={monthEarned} showHistoryLink />

          {/* Village snapshot + invite */}
          <div className="bg-[#FFFDF8] border border-[#EFE7D7] rounded-[18px] p-6">
            <CardHead icon={Compass} tint="teal" title="Your village snapshot" subtitle="Your journey so far" />
            <div className="grid grid-cols-3 gap-3.5">
              {[
                { n: totals?.events_booked, label: 'Events booked', top: 'border-t-[#B5C6BC]' },
                { n: totals?.events_attended, label: 'Attended', top: 'border-t-[#E9B9A7]' },
                { n: totals?.facilitators_followed, label: 'Facilitators', top: 'border-t-[#F2D29A]' },
              ].map((s) => (
                <div key={s.label} className={`bg-[#FFFDF8] border border-[#EFE7D7] border-t-[3px] ${s.top} rounded-2xl px-4 py-4`}>
                  <div className={`${FONT_HEADING} font-extrabold text-2xl text-[#2C2D2B]`}>{s.n ?? '—'}</div>
                  <div className={`${FONT_BODY} text-xs text-[#8A8475] mt-0.5`}>{s.label}</div>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-3.5 mt-4 bg-[#F7F0E6] border border-[#EFE7D7] rounded-2xl px-4 py-3.5">
              <div className="w-[38px] h-[38px] rounded-[11px] bg-[#F3DFBE] text-[#7a5414] flex items-center justify-center flex-shrink-0">
                <UserPlus className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className={`${FONT_UI} font-bold text-sm text-[#2C2D2B]`}>Invite a friend, earn together</div>
                <div className={`${FONT_BODY} text-xs text-[#8A8475]`}>You both earn PATH Coins on their first paid booking.</div>
              </div>
              <Link href="/me/invite" className={`${FONT_UI} flex-none font-bold text-[13px] text-[#2C2D2B] bg-[#F0B853] rounded-full px-4 py-2 shadow-[inset_0_0_0_1.4px_#D99E37] transition-all hover:-translate-y-px`}>
                Get your link
              </Link>
            </div>
          </div>
        </div>

        {/* ═══ Matches for you ═══ */}
        {recommendations.length > 0 && (
          <div className="mt-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-[34px] h-[34px] rounded-[11px] bg-[#EFD3C4] text-[#DE866D] flex items-center justify-center">
                <Sparkles className="h-4 w-4" />
              </div>
              <h2 className={`${FONT_HEADING} font-extrabold uppercase tracking-[.05em] text-lg text-[#2C2D2B]`}>Matches for you</h2>
              <Link href="/match" className={`${FONT_UI} ml-auto font-bold text-[13px] text-[#DE866D] hover:underline`}>
                Show me All →
              </Link>
            </div>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {recommendations.map((rec) => {
                const event = rec.event
                return (
                  <Link key={event.id} href={`/events/${event.id}`} className="group bg-[#FFFDF8] border border-[#EFE7D7] rounded-[18px] overflow-hidden transition-all hover:-translate-y-[3px] hover:shadow-[0_14px_30px_rgba(44,45,43,0.10)]">
                    <div className="relative h-[140px]">
                      <Image src={getFirstImageUrl(event.image_url, event.categories)} alt={event.title} fill className="object-cover transition-transform group-hover:scale-[1.02]" sizes="(max-width: 640px) 100vw, 33vw" />
                      {(rec.fitBucket ?? bucketFromScore(rec.relevanceScore)) && (
                        <span className="absolute top-2.5 left-2.5">
                          <FitBadge fitBucket={rec.fitBucket ?? bucketFromScore(rec.relevanceScore)} className={FONT_UI} />
                        </span>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className={`${FONT_UI} font-bold text-[15px] text-[#2C2D2B] line-clamp-2 mb-1.5`}>{event.title}</h3>
                      <div className={`${FONT_BODY} text-[13px] text-[#5E594F]`}>{formatShort(event)}</div>
                      <div className={`${FONT_BODY} text-[13px] text-[#5E594F] mb-2.5`}>{summarizeLocation(event.location)}</div>
                      {rec.reasoning && (
                        <div className="bg-[#F7F0E6] rounded-xl px-3.5 py-2.5">
                          <div className={`${FONT_BODY} font-bold text-[10px] tracking-[.08em] uppercase text-[#8A8475] mb-0.5`}>Why this matches you</div>
                          <p className={`${FONT_BODY} text-xs text-[#5E594F] line-clamp-2`}>{rec.reasoning}</p>
                        </div>
                      )}
                    </div>
                  </Link>
                )
              })}
            </div>
          </div>
        )}

        {/* ═══ Two-column: bookings/saved + following/reviews ═══ */}
        <div className="grid gap-5 lg:grid-cols-2 mt-6 items-start">
          <div className="space-y-5">
            {/* Upcoming bookings */}
            <div className="bg-[#FFFDF8] border border-[#EFE7D7] rounded-[18px] p-6">
              <CardHead icon={Calendar} tint="teal" title="Upcoming bookings" subtitle="Your next few sessions" link={{ label: 'All', href: '/bookings' }} />
              {bookings.length === 0 ? (
                <EmptyBlock icon={Calendar} title="No upcoming bookings" hint="Events you book will show up here." />
              ) : (
                bookings.map((event: any, i) => (
                  <Link key={event.id} href={event.booking_id ? `/bookings/${event.booking_id}` : `/events/${event.id}`} className={`flex items-center gap-3.5 py-3 ${i < bookings.length - 1 ? 'border-b border-[#EFE7D7]' : ''} hover:opacity-85 transition-opacity`}>
                    <div className="relative w-12 h-12 rounded-xl overflow-hidden flex-shrink-0">
                      <Image src={getFirstImageUrl(event.image_url, event.categories)} alt={event.title} fill className="object-cover" sizes="48px" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`${FONT_UI} font-bold text-sm text-[#2C2D2B] line-clamp-1`}>{event.title}</div>
                      <div className={`${FONT_BODY} text-xs text-[#8A8475] line-clamp-1`}>{formatShort(event)} · {summarizeLocation(event.location)}</div>
                    </div>
                    <span className={`${FONT_UI} flex-none font-bold text-[11px] text-[#3f6258] bg-[#D4D9CE] rounded-full px-2.5 py-1`}>
                      {event.booking_status === 'PENDING' ? 'Pending' : 'Confirmed'}
                    </span>
                  </Link>
                ))
              )}
            </div>

            {/* Saved events */}
            <div className="bg-[#FFFDF8] border border-[#EFE7D7] rounded-[18px] p-6">
              <CardHead icon={Heart} tint="terra" title="Saved events" subtitle="Bookmarked to revisit" count={savedEvents.length} link={savedEvents.length > 0 ? { label: 'All', href: '/discovery?saved=1' } : undefined} />
              {savedEvents.length === 0 ? (
                <EmptyBlock icon={Heart} title="No saved events yet" hint="Tap the heart on events you love." />
              ) : (
                savedEvents.map((event: any, i) => (
                  <Link key={event.id} href={`/events/${event.id}`} className={`flex items-center gap-3.5 py-3 ${i < savedEvents.length - 1 ? 'border-b border-[#EFE7D7]' : ''} hover:opacity-85 transition-opacity`}>
                    <div className="relative w-12 h-12 rounded-xl overflow-hidden flex-shrink-0">
                      <Image src={getFirstImageUrl(event.image_url, event.categories)} alt={event.title} fill className="object-cover" sizes="48px" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`${FONT_UI} font-bold text-sm text-[#2C2D2B] line-clamp-1`}>{event.title}</div>
                      <div className={`${FONT_BODY} text-xs text-[#8A8475] line-clamp-1`}>{formatShort(event)} · {summarizeLocation(event.location)}</div>
                    </div>
                  </Link>
                ))
              )}
            </div>
          </div>

          <div className="space-y-5">
            {/* Following / Friends */}
            <div className="bg-[#FFFDF8] border border-[#EFE7D7] rounded-[18px] p-6 relative">
              {/* Top-right toggle: facilitators you follow vs members you friended */}
              <div className="absolute right-6 top-6 z-10">
                <div className={`${FONT_UI} inline-flex rounded-full border border-[#EFE7D7] bg-white overflow-hidden text-[11px] font-bold`}>
                  <button
                    type="button"
                    onClick={() => setFollowTab('facilitators')}
                    className={`px-2.5 py-1 transition-colors ${followTab === 'facilitators' ? 'bg-[#DE866D] text-white' : 'text-[#8A8475] hover:bg-gray-50'}`}
                  >
                    Facilitators
                  </button>
                  <button
                    type="button"
                    onClick={() => setFollowTab('friends')}
                    className={`px-2.5 py-1 transition-colors border-l border-[#EFE7D7] ${followTab === 'friends' ? 'bg-[#DE866D] text-white' : 'text-[#8A8475] hover:bg-gray-50'}`}
                  >
                    Friends
                  </button>
                </div>
              </div>

              {followTab === 'facilitators' ? (
                <>
                  <CardHead icon={Users} tint="terra" title="Following" subtitle="Facilitators you follow" count={following.length} />
                  {following.length === 0 ? (
                    <EmptyBlock icon={Users} title="Not following anyone yet" hint="Follow facilitators to stay in the loop." />
                  ) : (
                    following.map((entry: any, i) => (
                      <Link key={entry.id} href={`/facilitator-profile/${entry.facilitator.user_id}`} className={`flex items-center gap-3.5 py-3 ${i < following.length - 1 ? 'border-b border-[#EFE7D7]' : ''} hover:opacity-85 transition-opacity`}>
                        {entry.facilitator.avatar_url ? (
                          <div className="relative w-11 h-11 rounded-full overflow-hidden flex-shrink-0">
                            <Image src={resolveImageUrl(entry.facilitator.avatar_url)} alt={entry.facilitator.full_name} fill className="object-cover" sizes="44px" />
                          </div>
                        ) : (
                          <div className={`${FONT_UI} w-11 h-11 rounded-full bg-[#D4D9CE] text-[#3f6258] flex items-center justify-center font-bold flex-shrink-0`}>
                            {entry.facilitator.full_name?.charAt(0)?.toUpperCase() ?? '?'}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className={`${FONT_UI} font-bold text-sm text-[#2C2D2B] line-clamp-1`}>{entry.facilitator.full_name}</div>
                          {entry.facilitator.facilitatorProfile?.professional_title && (
                            <div className={`${FONT_BODY} text-xs text-[#8A8475] line-clamp-1`}>{entry.facilitator.facilitatorProfile.professional_title}</div>
                          )}
                        </div>
                        <span className={`${FONT_UI} flex-none font-bold text-[11px] text-[#3f6a5e] border border-[#75A096] bg-[#75A096]/10 rounded-full px-2.5 py-1`}>Following</span>
                      </Link>
                    ))
                  )}
                </>
              ) : (
                <>
                  {/* #1166 FR-13 — heading only ("Friends" → "Following"); subtitle/link/route unchanged */}
                  <CardHead icon={Users} tint="terra" title="Following" subtitle="Members you've added" count={friends.length} link={{ label: 'Find friends', href: '/friends' }} />
                  {friends.length === 0 ? (
                    <EmptyBlock icon={Users} title="No friends yet" hint="Browse the Friends directory and add members you know." />
                  ) : (
                    friends.map((entry: any, i) => (
                      <Link key={entry.id} href={`/member-profile/${entry.facilitator.user_id}`} className={`flex items-center gap-3.5 py-3 ${i < friends.length - 1 ? 'border-b border-[#EFE7D7]' : ''} hover:opacity-85 transition-opacity`}>
                        {entry.facilitator.avatar_url ? (
                          <div className="relative w-11 h-11 rounded-full overflow-hidden flex-shrink-0">
                            <Image src={resolveImageUrl(entry.facilitator.avatar_url)} alt={entry.facilitator.full_name} fill className="object-cover" sizes="44px" />
                          </div>
                        ) : (
                          <div className={`${FONT_UI} w-11 h-11 rounded-full bg-[#D4D9CE] text-[#3f6258] flex items-center justify-center font-bold flex-shrink-0`}>
                            {entry.facilitator.full_name?.charAt(0)?.toUpperCase() ?? '?'}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className={`${FONT_UI} font-bold text-sm text-[#2C2D2B] line-clamp-1`}>{entry.facilitator.full_name}</div>
                          {entry.facilitator.pronouns && (
                            <div className={`${FONT_BODY} text-xs text-[#8A8475] line-clamp-1`}>{entry.facilitator.pronouns}</div>
                          )}
                        </div>
                        <span className={`${FONT_UI} flex-none font-bold text-[11px] text-[#b06046] border border-[#DE866D] bg-[#DE866D]/10 rounded-full px-2.5 py-1`}>Friend</span>
                      </Link>
                    ))
                  )}
                </>
              )}
            </div>

            {/* Reviews to leave */}
            <div className="bg-[#FFFDF8] border border-[#EFE7D7] rounded-[18px] p-6">
              <CardHead icon={Star} tint="gold" title="Reviews to leave" subtitle="Rate events you've attended" count={pendingReviews.length} link={pendingReviews.length > 0 ? { label: 'View all', href: '/bookings' } : undefined} />
              {pendingReviews.length === 0 ? (
                <EmptyBlock icon={Star} title="No reviews yet" hint="After attending an event, you can leave a review here — and earn coins for it." />
              ) : (
                pendingReviews.map((pending: any, i) => (
                  <button
                    key={pending.event_id}
                    type="button"
                    onClick={() => setReviewingEventId(pending.event_id)}
                    className={`w-full text-left flex items-center gap-3.5 py-3 ${i < pendingReviews.length - 1 ? 'border-b border-[#EFE7D7]' : ''} hover:opacity-85 transition-opacity`}
                  >
                    <div className="relative w-12 h-12 rounded-xl overflow-hidden flex-shrink-0">
                      <Image src={getFirstImageUrl(pending.event?.image_url, pending.event?.categories)} alt={pending.event?.title ?? 'Event'} fill className="object-cover" sizes="48px" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`${FONT_UI} font-bold text-sm text-[#2C2D2B] line-clamp-1`}>{pending.event?.title}</div>
                      <div className={`${FONT_BODY} text-xs text-[#8A8475]`}>Earn coins for sharing your experience</div>
                    </div>
                    <span className={`${FONT_UI} flex-none font-bold text-[11px] text-[#7a5414] bg-[#F3DFBE] rounded-full px-2.5 py-1`}>Write review</span>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>

      </div>

      {/* Write-a-review dialog — same flow as My Bookings. */}
      {reviewingEventId && accessToken && (() => {
        const pending = pendingReviews.find((p: any) => p.event_id === reviewingEventId)
        if (!pending) return null
        return (
          <ReviewModal
            pending={pending}
            accessToken={accessToken}
            onClose={() => setReviewingEventId(null)}
            onSubmitted={() => {
              setPendingReviews((prev) => prev.filter((p: any) => p.event_id !== reviewingEventId))
              setReviewingEventId(null)
            }}
          />
        )
      })()}

      {/* Visitors see the Home layout with a sign-up prompt on top.
          #1420 — exact copy from the issue (pinned in join-prompt-copy.ts);
          hideIncentiveNote avoids repeating the Path Coins line a second
          time, in different words, via the admin-managed fee-waiver box
          below. */}
      <AuthPromptDialog
        open={showVisitorPrompt}
        onOpenChange={setShowVisitorPrompt}
        title={JOIN_PROMPT_TITLE}
        description={
          <>
            {JOIN_PROMPT_DESCRIPTION.split('\n').map((line, i) => (
              <Fragment key={i}>
                {line}
                <br />
              </Fragment>
            ))}
          </>
        }
        icon={JOIN_PROMPT_ICON}
        hideIncentiveNote
      />
    </div>
  )
}
